import {
  AGE_GROUPS,
  tournamentConfigSchema,
  type AgeGroup,
  type SelfServeTournamentBody,
  type TournamentConfig,
} from "@alpha/shared";
import { stateTimezone } from "./timezones";

/** The four screens. 1 name, 2 dates, 3 place, 4 age groups. */
export type SelfServeStep = 1 | 2 | 3 | 4;

export interface SelfServeDraft {
  name: string;
  startDate: string;
  endDate: string;
  /** City. The API field is `location`. */
  location: string;
  state: string;
  /**
   * The IANA zone every game time is entered and shown in. Picking a state sets
   * it (`withState`); the coach changes it when their fields keep another zone.
   */
  timezone: string;
  ageGroups: AgeGroup[];
  /** Closed means "keep the defaults" and the payload omits config. */
  rulesOpen: boolean;
  config: TournamentConfig;
}

export function emptySelfServeDraft(): SelfServeDraft {
  return {
    name: "",
    startDate: "",
    endDate: "",
    location: "",
    state: "",
    timezone: "",
    ageGroups: [],
    rulesOpen: false,
    config: tournamentConfigSchema.parse({}),
  };
}

/**
 * Picking a state sets the zone to that state's, over one the coach chose by
 * hand too: that choice was about the state being replaced, and the zone select
 * showing the new default sits right under the state.
 */
export function withState(draft: SelfServeDraft, state: string): SelfServeDraft {
  if (state === draft.state) return draft;
  return { ...draft, state, timezone: stateTimezone(state) };
}

/** Null when the dates are in order or not both filled in yet. */
export function dateError(draft: SelfServeDraft): string | null {
  if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
    return "The end date is before the start date";
  }
  return null;
}

export function canAdvance(draft: SelfServeDraft, step: SelfServeStep): boolean {
  switch (step) {
    case 1:
      return draft.name.trim().length > 0;
    case 2:
      return Boolean(draft.startDate && draft.endDate && !dateError(draft));
    case 3:
      return (
        draft.location.trim().length > 0 && draft.state.length > 0 && draft.timezone.length > 0
      );
    case 4:
      // An emptied rules field is NaN, which the API would refuse with a 400.
      return (
        draft.ageGroups.length > 0 &&
        (!draft.rulesOpen || tournamentConfigSchema.safeParse(draft.config).success)
      );
    default: {
      const never: never = step;
      return never;
    }
  }
}

/**
 * The POST body. Age groups go out in 6U–18U order with duplicates removed,
 * which is the same order the server uses to name divisions.
 */
export function selfServePayload(draft: SelfServeDraft): SelfServeTournamentBody {
  const ageGroups = AGE_GROUPS.filter((g) => draft.ageGroups.includes(g));
  const base = {
    name: draft.name.trim(),
    startDate: draft.startDate,
    endDate: draft.endDate,
    location: draft.location.trim(),
    state: draft.state,
    ageGroups,
    timezone: draft.timezone,
  };
  return draft.rulesOpen ? { ...base, config: draft.config } : base;
}
